import React, { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  CircularProgress,
  Chip,
} from '@mui/material';
import { 
  DateRange as DateRangeIcon, 
  Refresh as RefreshIcon,
  ArrowBack as ArrowBackIcon,
  LocationOn as LocationOnIcon,
} from '@mui/icons-material';
import api from '../../services/api';

const CollectionsAudit = () => {
  const [startDate, setStartDate] = useState(() => {
    const today = new Date();
    const thirtyDaysAgo = new Date(today);
    thirtyDaysAgo.setDate(today.getDate() - 30);
    return thirtyDaysAgo.toISOString().slice(0, 10);
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [view, setView] = useState('areas'); // 'areas' or 'details'
  const [selectedArea, setSelectedArea] = useState(null);
  const [areaSummaries, setAreaSummaries] = useState([]);
  const [detailedCollections, setDetailedCollections] = useState([]);
  const [totalCollected, setTotalCollected] = useState(0);

  const validateDates = () => {
    if (!startDate || !endDate) {
      setError('Please select both start and end dates');
      return false;
    }
    const start = new Date(startDate);
    const end = new Date(endDate);
    if (end < start) {
      setError('End date cannot be before start date');
      return false;
    }
    return true;
  };

  const fetchAreaSummaries = useCallback(async () => {
    if (!validateDates()) {
      return;
    }
    try {
      setLoading(true);
      setError('');
      setView('areas');
      setSelectedArea(null);
      const response = await api.get('/admin/reports/collections-audit/areas', {
        params: { 
          startDate: startDate.trim(), 
          endDate: endDate.trim() 
        },
      });
      setAreaSummaries(response.data.areas || []);
      setTotalCollected(response.data.totalCollected || 0);
    } catch (err) {
      console.error('Error fetching area summaries:', err);
      setError(err.response?.data?.error || err.message || 'Failed to load data');
      setAreaSummaries([]);
      setTotalCollected(0);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    fetchAreaSummaries();
  }, [fetchAreaSummaries]);

  const fetchAreaDetails = useCallback(async (areaId) => {
    if (!validateDates()) {
      return;
    }
    if (areaId === null || areaId === undefined) {
      setError('Area is required');
      return;
    }
    try {
      setLoading(true);
      setError('');
      setSelectedArea(areaId);
      setView('details');
      const response = await api.get('/admin/reports/collections-audit', {
        params: { 
          startDate: startDate.trim(), 
          endDate: endDate.trim(), 
          areaId: areaId 
        },
      });
      setDetailedCollections(response.data.collections || []);
    } catch (err) {
      console.error('Error fetching area details:', err);
      setError(err.response?.data?.error || err.message || 'Failed to load data');
      setDetailedCollections([]);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  const handleBackToAreas = () => {
    setView('areas');
    setSelectedArea(null);
    setDetailedCollections([]);
    // Refresh area summaries when going back
    fetchAreaSummaries();
  };

  const handleApplyDates = () => {
    if (view === 'areas') {
      fetchAreaSummaries();
    } else if (selectedArea) {
      fetchAreaDetails(selectedArea);
    } else {
      // If in details view but no area selected, go back to areas
      setView('areas');
      fetchAreaSummaries();
    }
  };

  const formattedCurrency = (amount) =>
    typeof amount === 'number' || typeof amount === 'string'
      ? Number(amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      : (amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const formatDateTime = (date, time) => {
    if (time) {
      const dt = new Date(time);
      return dt.toLocaleString();
    }
    if (date) {
      return new Date(date).toLocaleDateString();
    }
    return 'N/A';
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {view === 'details' && (
            <Button
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={handleBackToAreas}
            >
              Back to Areas
            </Button>
          )}
          <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
            Collections Audit
            {selectedArea && areaSummaries.find(a => a.areaId === selectedArea) && (
              <Chip 
                label={areaSummaries.find(a => a.areaId === selectedArea)?.areaName || 'Unknown'} 
                icon={<LocationOnIcon />}
                color="primary"
                sx={{ ml: 2 }}
              />
            )}
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="primary"
          startIcon={<RefreshIcon />}
          onClick={handleApplyDates}
          disabled={loading}
        >
          Refresh
        </Button>
      </Box>

      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4} md={3}>
            <TextField
              label="From Date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={4} md={3}>
            <TextField
              label="To Date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={4} md={3}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<DateRangeIcon />}
              onClick={handleApplyDates}
              disabled={loading || !startDate || !endDate}
              fullWidth
              sx={{ height: '56px' }}
            >
              Apply
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {view === 'areas' ? (
        <>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={4}>
              <Paper
                elevation={3}
                sx={{
                  p: 2,
                  backgroundColor: 'primary.main',
                  color: 'white',
                }}
              >
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Total Collected
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                  Rs {formattedCurrency(totalCollected)}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.8, mt: 1 }}>
                  {startDate} to {endDate}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Paper
                elevation={3}
                sx={{
                  p: 2,
                  backgroundColor: 'success.main',
                  color: 'white',
                }}
              >
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Total Areas
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                  {areaSummaries.length}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.8, mt: 1 }}>
                  Click an area to view details
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          <TableContainer component={Paper} elevation={3}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: 'primary.main' }}>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>#</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Area</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Total Collected</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Collections</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                      <CircularProgress sx={{ mr: 2 }} />
                      <Typography variant="body1" color="text.secondary">
                        Loading areas...
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : areaSummaries.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">
                        No collections found for the selected period.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  areaSummaries.map((area, index) => (
                    <TableRow 
                      key={area.areaId || 'Unknown'}
                      sx={{ 
                        cursor: 'pointer',
                        '&:hover': {
                          backgroundColor: 'action.hover',
                        }
                      }}
                      onClick={() => {
                        if (validateDates()) {
                          fetchAreaDetails(area.areaId);
                        }
                      }}
                    >
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <LocationOnIcon color="primary" />
                          <strong>{area.areaName || 'Unknown'}</strong>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <strong>Rs {formattedCurrency(area.totalCollected)}</strong>
                      </TableCell>
                      <TableCell>{area.collectionCount}</TableCell>
                      <TableCell>
                        <Button 
                          variant="outlined" 
                          color="primary" 
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            if (validateDates()) {
                              fetchAreaDetails(area.areaId);
                            }
                          }}
                        >
                          View Details
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      ) : (
        <>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={4}>
              <Paper
                elevation={3}
                sx={{
                  p: 2,
                  backgroundColor: 'primary.main',
                  color: 'white',
                }}
              >
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Area Total
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                  Rs {formattedCurrency(
                    detailedCollections.reduce((sum, c) => sum + (Number(c.collectedAmount) || 0), 0)
                  )}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.8, mt: 1 }}>
                  {areaSummaries.find(a => a.areaId === selectedArea)?.areaName || 'Unknown'}
                </Typography>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Paper
                elevation={3}
                sx={{
                  p: 2,
                  backgroundColor: 'info.main',
                  color: 'white',
                }}
              >
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Total Records
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                  {detailedCollections.length}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.8, mt: 1 }}>
                  Collection transactions
                </Typography>
              </Paper>
            </Grid>
          </Grid>

          <TableContainer component={Paper} elevation={3}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: 'primary.main' }}>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>#</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Collector</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Loan ID</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Customer</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>NIC</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Installment #</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Status</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }} align="right">Collected Amount</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Payment Date</TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Timestamp</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 6 }}>
                      <CircularProgress sx={{ mr: 2 }} />
                      <Typography variant="body1" color="text.secondary">
                        Loading details...
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : detailedCollections.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">
                        No collections found for {areaSummaries.find(a => a.areaId === selectedArea)?.areaName || 'this area'} in the selected period.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  detailedCollections.map((row, index) => (
                    <TableRow key={`${row.paymentHistoryId || row.loanId}-${index}`} hover>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>{row.collectorName || 'Unknown'}</TableCell>
                      <TableCell><strong>#{row.loanId}</strong></TableCell>
                      <TableCell>{row.customerName || 'N/A'}</TableCell>
                      <TableCell>{row.customerNIC || 'N/A'}</TableCell>
                      <TableCell>
                        {row.installmentNumber ? `#${row.installmentNumber}` : 'N/A'}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={row.installmentStatus || 'N/A'}
                          color={
                            row.installmentStatus === 'PAID' ? 'success' :
                            row.installmentStatus === 'PARTIAL' ? 'warning' :
                            row.installmentStatus === 'OVERDUE' ? 'error' : 'default'
                          }
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        <strong style={{ color: '#2e7d32', fontSize: '1rem' }}>
                          Rs {formattedCurrency(row.collectedAmount)}
                        </strong>
                      </TableCell>
                      <TableCell>{row.paidDate ? formatDateTime(row.paidDate) : 'N/A'}</TableCell>
                      <TableCell>{formatDateTime(null, row.paidTimestamp)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  );
};

export default CollectionsAudit;
